import { LinkIcon, XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useRef, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { cn } from '@/shared/lib/cn';
import { isEditableTarget } from '@/shared/lib/dom';
import { Button } from '@/shared/ui/button';
import { panelLayer } from '@/shared/ui/layers';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { SidePanelContext, useSidePanelContext } from './context';

export type SidePanelSize = 'md' | 'lg' | 'xl' | 'full';

/**
 * Esc inside a text field belongs to the field first (cancel inline edit, close a mention popup).
 * The panel stays open; a plain field is blurred so the next Esc closes the panel.
 */
function keepOpenWhileEditing(event: KeyboardEvent): void {
  const target = event.target;
  if (!isEditableTarget(target)) return;
  event.preventDefault();
  const element = target as HTMLElement;
  const editorWithSuggestion = element.closest('.ProseMirror')?.querySelector('.suggestion');
  if (editorWithSuggestion) return;
  setTimeout(() => {
    if (document.activeElement === element) element.blur();
  }, 0);
}

/* `full` is as wide as the screen allows: --panel-width is capped at the viewport minus 3.5rem, which keeps the close tab on the left edge visible. */
const SIZE_WIDTH: Record<SidePanelSize, string> = { md: '40rem', lg: '56rem', xl: '72rem', full: '100vw' };
/* Each stacked level is 2.75rem narrower (see the width class below), so the edge of the panel below stays visible. */

export interface SidePanelProps {
  open: boolean;
  /** Called with `false` on Esc, outside click or the close button. */
  onOpenChange: (open: boolean) => void;
  /** Accessible name (visually hidden; use SidePanelHeader for the visible title). */
  title: string;
  children: ReactNode;
  size?: SidePanelSize;
  /** Position in the stack (0 = bottom). Deeper panels are narrower and dim the ones below. */
  depth?: number;
  /** Show the "copy link" button (URL-addressable panels). */
  shareable?: boolean;
  /** Fired when the exit animation finished. */
  onExited?: () => void;
  /**
   * Modal (default): dims the page, keeps the focus inside, closes on outside clicks. Non-modal: the page stays usable
   * beside it (no overlay, outside clicks go to the page, the focus stays where it is; Esc pressed in the page is the
   * page's).
   */
  modal?: boolean;
  className?: string;
}

/**
 * Right-side "slider" in the spirit of Bitrix24: slides in over the page, stacks over other panels
 * (each deeper one narrower), closes with Esc / outside click / the tab on its left edge.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  children,
  size = 'lg',
  depth = 0,
  shareable = false,
  onExited,
  modal = true,
  className,
}: SidePanelProps) {
  const { t } = useTranslation('shared');
  const contentRef = useRef<HTMLDivElement>(null);
  const close = () => onOpenChange(false);
  const style = {
    '--panel-width': `min(${SIZE_WIDTH[size]}, calc(100vw - 3.5rem))`,
    '--panel-depth': depth,
    zIndex: panelLayer(depth, 1),
  } as CSSProperties;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('sidePanel.linkCopied'));
    } catch {
      toast.error(t('errors.unknown'));
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogPrimitive.Portal>
        {modal && (
          <DialogPrimitive.Overlay
            className={cn(
              'fixed inset-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
              depth === 0 ? 'bg-overlay' : 'bg-black/10',
            )}
            style={{ zIndex: panelLayer(depth, 0) }}
          />
        )}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-slot="side-panel"
          data-depth={depth}
          data-modal={modal || undefined}
          style={style}
          ref={contentRef}
          onEscapeKeyDown={(event) => {
            // Non-modal: Esc pressed in the page (a canvas clearing its selection, a field) belongs to the page; from
            // the panel or with nothing focused it closes the panel.
            const active = document.activeElement;
            if (!modal && active && active !== document.body && !contentRef.current?.contains(active)) {
              event.preventDefault();
              return;
            }
            keepOpenWhileEditing(event);
          }}
          onInteractOutside={(event) => {
            if (!modal) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            // Focus the panel itself rather than its first button: that button has a tooltip, which would
            // open on focus and swallow the first Esc. A field focused by the panel (autoFocus) keeps focus.
            // A non-modal panel leaves the focus where it is (the page opening it moves it when asked to).
            event.preventDefault();
            if (!modal) return;
            const content = contentRef.current;
            if (content && !content.contains(document.activeElement)) content.focus({ preventScroll: true });
          }}
          onAnimationEnd={(e) => {
            if (e.currentTarget === e.target && e.currentTarget.dataset.state === 'closed') onExited?.();
          }}
          className={cn(
            // Width: preferred size minus one stack step per depth level (see STACK_STEP).
            'fixed inset-y-0 right-0 flex w-screen flex-col bg-background text-foreground shadow-panel outline-none sm:w-[calc(var(--panel-width)-var(--panel-depth)*2.75rem)] sm:rounded-l-2xl',
            'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:ease-[var(--ease-in-out-soft)] data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:ease-[var(--ease-out-soft)] data-[state=open]:slide-in-from-right',
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {/* Floating tab on the left edge (desktop): close + copy link. */}
          <div className="absolute top-4 -left-11 hidden flex-col gap-1.5 sm:flex">
            <SimpleTooltip content={t('actions.close')} side="left" shortcut="Esc">
              <button
                type="button"
                onClick={close}
                aria-label={t('actions.close')}
                className="flex size-9 items-center justify-center rounded-l-xl rounded-r-md bg-background/95 text-muted-foreground shadow-md ring-1 focus-ring ring-border backdrop-blur transition-colors hover:text-foreground"
              >
                <XIcon className="size-4" />
              </button>
            </SimpleTooltip>
            {shareable && (
              <SimpleTooltip content={t('sidePanel.copyLink')} side="left">
                <button
                  type="button"
                  onClick={() => void copyLink()}
                  aria-label={t('sidePanel.copyLink')}
                  className="flex size-9 items-center justify-center rounded-l-xl rounded-r-md bg-background/95 text-muted-foreground shadow-md ring-1 focus-ring ring-border backdrop-blur transition-colors hover:text-foreground"
                >
                  <LinkIcon className="size-4" />
                </button>
              </SimpleTooltip>
            )}
          </div>
          <SidePanelContext value={{ depth, close }}>{children}</SidePanelContext>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface SidePanelHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Leading visual (icon, avatar). */
  leading?: ReactNode;
  actions?: ReactNode;
  /** Tabs / meta row under the title. */
  children?: ReactNode;
  className?: string;
}

/** Sticky panel header: title, description, actions and a close button on small screens. */
export function SidePanelHeader({
  title,
  description,
  leading,
  actions,
  children,
  className,
}: SidePanelHeaderProps) {
  const { t } = useTranslation('shared');
  const { close } = useSidePanelContext();
  return (
    <div className={cn('shrink-0 border-b px-5 pt-4 pb-3 sm:px-6', className)}>
      <div className="flex items-start gap-3">
        {leading && <div className="shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          <h2 className="text-lg leading-snug font-semibold tracking-[-0.015em] break-words">{title}</h2>
          {description && <div className="mt-0.5 text-sm text-muted-foreground">{description}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={close}
            aria-label={t('actions.close')}
            className="sm:hidden"
          >
            <XIcon />
          </Button>
        </div>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

export function SidePanelBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6', className)}>{children}</div>;
}

export function SidePanelFooter({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end gap-2 border-t bg-surface/60 px-5 py-3 sm:px-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
